"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { parseDate } from "chrono-node/en";
import { Suspense, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ProjectRequestFormData, projectRequestFormSchema, projectTypes } from "@/lib/forms/project-request";
import { AnimatePresenceWrapper } from "../animate-presence-wrapper";
import { CircularStepProgress } from "../circular-step-progress";
import { RangeSteps } from "../range-steps";
import { Button } from "../ui/button";
import { Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet, FieldTitle } from "../ui/field";
import { Input } from "../ui/input";
import { NaturalLanguageDatePicker } from "../ui/natural-language-date-picker";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Skeleton } from "../ui/skeleton";
import { Slider } from "../ui/slider";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";

/**
 * A multi-step form for requesting a project. Once the user submits, it sends an email to me.
 */
function ProjectRequestForm() {
  const $ = _c(76);
  const [step, setStep] = useState(0);
  useState(null);
  useState("");
  useState(undefined);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = zodResolver(projectRequestFormSchema);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseDate("In 1 week");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      resolver: t0,
      defaultValues: {
        projectType: "website",
        budget: 250,
        dateNeededBy: t1 as Date,
        name: "",
        email: "",
        description: ""
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    handleSubmit,
    control,
    formState: t3,
    setValue,
    trigger
  } = useForm(t2);
  const {
    isSubmitting
  } = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async function onSubmit(data) {
      setStep("submitting");
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const onSubmit = t4;
  let t5;
  if ($[4] !== handleSubmit) {
    t5 = handleSubmit(onSubmit);
    $[4] = handleSubmit;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = step === 0;
  let t7;
  if ($[6] !== control) {
    t7 = <Controller control={control} name="projectType" render={_temp} />;
    $[6] = control;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <AnimatePresenceWrapper childKey="step-1" show={t6}>{t7}</AnimatePresenceWrapper>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = step === 1;
  let t10;
  if ($[11] !== setValue) {
    t10 = t11 => {
      const {
        field: field_0,
        fieldState: fieldState_0
      } = t11;
      return <Field className="w-full flex flex-col gap-4 my-10" data-invalid={fieldState_0.invalid}><FieldLabel className="w-full flex text-lg font-normal md:text-2xl" htmlFor="budget-slider">Your budget:<span className="font-bold">{new Intl.NumberFormat("en-GB", {
              style: "currency",
              currency: "GBP"
            }).format(field_0.value)}</span></FieldLabel><Slider value={[field_0.value]} min={250} max={10000} step={250} onValueChange={value => setValue("budget", value[0])} className="w-full" id="budget-slider" /><RangeSteps min={250} max={10000} step={250} className="px-0.5" />{fieldState_0.invalid && <FieldError errors={[fieldState_0.error]} />}</Field>;
    };
    $[11] = setValue;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== control || $[14] !== t10) {
    t11 = <Controller control={control} name="budget" render={t10} />;
    $[13] = control;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t9) {
    t12 = <AnimatePresenceWrapper childKey="step-2" show={t9}>{t11}</AnimatePresenceWrapper>;
    $[16] = t11;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = step === 2;
  let t14;
  if ($[19] !== setValue) {
    t14 = t15 => {
      const {
        fieldState: fieldState_1
      } = t15;
      return <Field className="flex flex-col gap-4" data-invalid={fieldState_1.invalid}><FieldContent><Suspense fallback={<Skeleton />}><NaturalLanguageDatePicker label="When do you need it by?" endMonth={new Date(new Date().getFullYear() + 1, 12)} onChangeAction={date => setValue("dateNeededBy", date)} /></Suspense></FieldContent><FieldDescription className="italic">This only includes business days.</FieldDescription>{fieldState_1.invalid && <FieldError errors={[fieldState_1.error]} />}</Field>;
    };
    $[19] = setValue;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== control || $[22] !== t14) {
    t15 = <Controller control={control} name="dateNeededBy" render={t14} />;
    $[21] = control;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t13 || $[25] !== t15) {
    t16 = <AnimatePresenceWrapper childKey="step-3" show={t13}>{t15}</AnimatePresenceWrapper>;
    $[24] = t13;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = step === 3;
  let t18;
  if ($[27] !== control) {
    t18 = <Controller control={control} name="description" render={_temp2} />;
    $[27] = control;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17 || $[30] !== t18) {
    t19 = <AnimatePresenceWrapper childKey="step-4" show={t17}>{t18}</AnimatePresenceWrapper>;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = step === 4;
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <h3 className="text-lg font-bold md:text-2xl">How should I contact you?</h3>;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== control) {
    t22 = <div className="flex flex-col gap-4">{t21}<Controller control={control} name="name" render={_temp3} /><Controller control={control} name="email" render={_temp4} /></div>;
    $[33] = control;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t20 || $[36] !== t22) {
    t23 = <AnimatePresenceWrapper childKey="step-5" show={t20}>{t22}</AnimatePresenceWrapper>;
    $[35] = t20;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t12 || $[39] !== t16 || $[40] !== t19 || $[41] !== t23 || $[42] !== t8) {
    t24 = <FieldGroup>{t8}{t12}{t16}{t19}{t23}</FieldGroup>;
    $[38] = t12;
    $[39] = t16;
    $[40] = t19;
    $[41] = t23;
    $[42] = t8;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== step) {
    t25 = () => {
      if (typeof step === "string") {
        setStep(0);
        return;
      }
      setStep(step === 0 ? 0 : step - 1);
    };
    $[44] = step;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = step === 0 || typeof step === "string" ? "hidden" : "";
  let t27;
  if ($[46] !== t25 || $[47] !== t26) {
    t27 = <Button type="button" onClick={t25} className={t26}>Back</Button>;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  const t28 = step === 4 ? "" : "hidden";
  let t29;
  if ($[49] !== isSubmitting) {
    t29 = isSubmitting && <Spinner />;
    $[49] = isSubmitting;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== isSubmitting || $[52] !== t28 || $[53] !== t29) {
    t30 = <Button type="submit" form="email-form" className={t28} disabled={isSubmitting}>{t29}Send request</Button>;
    $[51] = isSubmitting;
    $[52] = t28;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  const t31 = step === 4 || typeof step === "string" ? "hidden" : "";
  let t32;
  if ($[55] !== step || $[56] !== trigger) {
    t32 = async () => {
      if (typeof step !== "number") {
        return;
      }
      bb80: switch (step) {
        case 0:
          {
            const isValid = await trigger("projectType");
            if (!isValid) {
              return;
            }
            break bb80;
          }
        case 1:
          {
            const budgetValid = await trigger("budget");
            if (!budgetValid) {
              return;
            }
            break bb80;
          }
        case 2:
          {
            const dateValid = await trigger("dateNeededBy");
            if (!dateValid) {
              return;
            }
            break bb80;
          }
        case 3:
          {
            const messageValid = await trigger("description");
            if (!messageValid) {
              return;
            }
          }
      }
      setStep(step + 1);
    };
    $[55] = step;
    $[56] = trigger;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t31 || $[59] !== t32) {
    t33 = <Button type="button" id="next-project-request-step" className={t31} onClick={t32}>Next</Button>;
    $[58] = t31;
    $[59] = t32;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t30 || $[62] !== t33) {
    t34 = <div className="w-full flex justify-end relative">{t30}{t33}</div>;
    $[61] = t30;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== t27 || $[65] !== t34) {
    t35 = <div className="flex items-center justify-between">{t27}{t34}</div>;
    $[64] = t27;
    $[65] = t34;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t24 || $[68] !== t35 || $[69] !== t5) {
    t36 = <form onSubmit={t5} className="w-xs flex flex-col space-y-4 md:w-4xl" id="email-form">{t24}{t35}</form>;
    $[67] = t24;
    $[68] = t35;
    $[69] = t5;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== step) {
    t37 = <CircularStepProgress step={step} />;
    $[71] = step;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t36 || $[74] !== t37) {
    t38 = <div className="flex items-center gap-5 xl:w-6xl">{t36}{t37}</div>;
    $[73] = t36;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  return t38;
}
function _temp4(t0) {
  const {
    field: field_3,
    fieldState: fieldState_4
  } = t0;
  return <Field className="w-xs flex-col md:w-sm"><FieldLabel className="w-full flex text-lg" htmlFor="requestor-email">Email</FieldLabel><Input {...field_3} aria-invalid={fieldState_4.invalid} id="requestor-email" placeholder="john.doe@example.com" type="email" disabled={field_3.disabled} /><FieldDescription>Your email address</FieldDescription>{fieldState_4.invalid && <FieldError errors={[fieldState_4.error]} />}</Field>;
}
function _temp3(t0) {
  const {
    field: field_2,
    fieldState: fieldState_3
  } = t0;
  return <Field className="w-xs flex flex-col md:w-sm" data-invalid={fieldState_3.invalid}><FieldLabel className="w-full flex text-lg" htmlFor="requestor-name">Name</FieldLabel><Input {...field_2} aria-invalid={fieldState_3.invalid} placeholder="John Doe" disabled={field_2.disabled} id="requestor-name" /><FieldDescription className="flex">Your full name</FieldDescription>{fieldState_3.invalid && <FieldError errors={[fieldState_3.error]} className="flex" />}</Field>;
}
function _temp2(t0) {
  const {
    field: field_1,
    fieldState: fieldState_2
  } = t0;
  return <Field className="flex flex-col gap-4"><FieldLabel className="w-full flex text-lg font-bold md:text-2xl">What do you want from your website?</FieldLabel><Textarea {...field_1} placeholder="I want a beautiful website..." disabled={field_1.disabled} />{fieldState_2.invalid && <FieldError errors={[fieldState_2.error]} />}</Field>;
}
function _temp(t0) {
  const {
    field,
    fieldState
  } = t0;
  return <FieldSet className="w-full flex flex-col gap-4" data-invalid={fieldState.invalid}><FieldLegend className="w-full flex flex-col text-lg font-bold md:text-2xl">What kind of project are you looking for?</FieldLegend><RadioGroup name={field.name} value={field.value} aria-invalid={fieldState.invalid} onValueChange={field.onChange}>{projectTypes.map((projectType, i) => <FieldLabel key={i} htmlFor={`radiogroup-${projectType.type}`}><Field orientation="horizontal" data-invalid={fieldState.invalid}><FieldContent><FieldTitle>{projectType.type}</FieldTitle><FieldDescription>{projectType.description}</FieldDescription></FieldContent><RadioGroupItem value={projectType.type} id={`radiogroup-${projectType.type}`} aria-invalid={fieldState.invalid} /></Field></FieldLabel>)}</RadioGroup>{fieldState.invalid && <FieldError errors={[fieldState.error]} />}</FieldSet>;
}
export { ProjectRequestForm };